import * as THREE from 'three';
import { EffectComposer } from 'three/addons/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/addons/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/addons/postprocessing/UnrealBloomPass.js';
import { OutputPass } from 'three/addons/postprocessing/OutputPass.js';
import { ShaderPass } from 'three/addons/postprocessing/ShaderPass.js';
import { scene, camera, renderer } from '../scene.js';

// Bloom layer for selective bloom (payloads)
export const BLOOM_LAYER = 1;

// Bloom configuration
export const bloomConfig = {
  enabled: false, // Default OFF
  strength: 1.5,
  radius: 0.4,
  threshold: 0.2
};

let composer = null;
let renderPass = null;
let bloomComposer = null;
let bloomPass = null;
let bloomRenderPass = null;
let outputPass = null;
let bloomLayer = null;
let materials = null;

// Darken non-bloom objects
function darkenNonBloomed(obj) {
  if (obj.isMesh && obj.material) {
    materials.set(obj, obj.material);
    
    if (obj.material.isPointsMaterial) {
      // For point materials, we'll handle differently
      return;
    }
    
    // Create dark material for non-bloom objects
    const darkMaterial = new THREE.MeshBasicMaterial({ color: 'black' });
    obj.material = darkMaterial;
  }
}

// Restore original materials
function restoreMaterial(obj) {
  if (materials.has(obj)) {
    obj.material = materials.get(obj);
    materials.delete(obj);
  }
}

export function initComposer() {
  if (!renderer || !scene || !camera) {
    console.error('Renderer, scene, or camera not initialized');
    return null;
  }

  const width = renderer.domElement.width;
  const height = renderer.domElement.height;
  const resolution = new THREE.Vector2(width, height);

  // Store original materials for restoration
  materials = new Map();
  bloomLayer = new THREE.Layers();
  bloomLayer.set(BLOOM_LAYER);

  // Main composer - renders scene (bloom objects will be excluded via composite)
  composer = new EffectComposer(renderer);
  
  // Render pass - renders the scene normally (all layers)
  renderPass = new RenderPass(scene, camera);
  composer.addPass(renderPass);

  // Separate composer for bloom objects only
  bloomComposer = new EffectComposer(renderer);
  
  // Render pass for bloom layer only
  bloomRenderPass = new RenderPass(scene, camera);
  bloomComposer.addPass(bloomRenderPass);

  // Bloom pass - applies bloom to bloom layer only
  bloomPass = new UnrealBloomPass(
    resolution,
    bloomConfig.strength,
    bloomConfig.radius,
    bloomConfig.threshold
  );
  bloomComposer.addPass(bloomPass);

  // Composite pass - combines main scene with bloomed objects
  const compositeMaterial = new THREE.ShaderMaterial({
    uniforms: {
      baseTexture: { value: null },
      bloomTexture: { value: bloomComposer.renderTarget2.texture }
    },
    vertexShader: `
      varying vec2 vUv;
      void main() {
        vUv = uv;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: `
      uniform sampler2D baseTexture;
      uniform sampler2D bloomTexture;
      varying vec2 vUv;
      void main() {
        vec4 base = texture2D(baseTexture, vUv);
        vec4 bloom = texture2D(bloomTexture, vUv);
        gl_FragColor = base + bloom;
      }
    `,
    defines: {}
  });
  
  const compositePass = new ShaderPass(compositeMaterial, 'baseTexture');
  compositePass.needsSwap = true;
  composer.addPass(compositePass);
  
  // Update bloom texture reference when bloom composer renders
  const originalBloomRender = bloomComposer.render.bind(bloomComposer);
  bloomComposer.render = function() {
    originalBloomRender();
    compositeMaterial.uniforms.bloomTexture.value = bloomComposer.renderTarget2.texture;
  };

  // Output pass - tone mapping and final output
  outputPass = new OutputPass();
  composer.addPass(outputPass);

  console.log('Post-processing composer initialized with selective bloom');
  return composer;
}

export function getComposer() {
  return composer;
}

// Render function that handles selective bloom
export function renderSelectiveBloom() {
  // If bloom disabled, just render normally
  if (!bloomConfig.enabled) {
    renderer.render(scene, camera);
    return;
  }
  
  if (!composer || !bloomComposer) return;
  
  // Store original camera layer mask
  const originalLayers = camera.layers.mask;
  
  // First, render bloom objects only (layer 1) to get bloom texture
  camera.layers.mask = 1 << BLOOM_LAYER;
  bloomComposer.render();
  
  // Then render main scene with all layers EXCEPT bloom layer
  // This prevents bloom objects from appearing in the base texture (they'll be added via bloom)
  // Enable all layers except bloom layer
  camera.layers.mask = 0xffffffff & ~(1 << BLOOM_LAYER);
  composer.render();
  
  // Restore original camera layers
  camera.layers.mask = originalLayers;
}

export function updateBloomConfig() {
  if (bloomPass) {
    bloomPass.strength = bloomConfig.strength;
    bloomPass.radius = bloomConfig.radius;
    bloomPass.threshold = bloomConfig.threshold;
  }
}

export function resizeComposer() {
  if (!composer || !bloomComposer || !renderer) return;
  
  const width = renderer.domElement.width;
  const height = renderer.domElement.height;
  
  composer.setSize(width, height);
  bloomComposer.setSize(width, height);
  
  // Update bloom pass resolution
  if (bloomPass) {
    bloomPass.setSize(width, height);
  }
}
